import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { toast } from 'react-toastify';
import { AppLayout } from '../../../components/layouts';
import PageHeader from '../../../components/dashboard/PageHeader';
import { CustomModal } from '../../../components/ui';
import { modalShell } from '../../../components/ui/modalDesign';
import PersonalClassModal, { type PersonalClassModalHandle } from '../home/PersonalClassModal';
import UserService from '../../../services/user.service';
import { startPersonalCheckout } from '../../../utils/personalCheckout';

function slotList(value: unknown): any[] {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try {
            return slotList(JSON.parse(value));
        } catch {
            return [];
        }
    }
    if (value && typeof value === 'object') return Object.values(value);
    return [];
}

function formatWhen(value?: string) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function statusText(status: string) {
    if (status === 'active') return 'Active';
    if (status === 'payment_due' || status === 'renewal_due') return 'Renewal due';
    if (status === 'expired') return 'Expired';
    if (status === 'paused') return 'Paused';
    if (status === 'cancelled') return 'Cancelled';
    return 'Unpaid';
}

function statusPill(status: string) {
    if (status === 'active') return 'bg-emerald-50 text-emerald-700';
    if (status === 'paused') return 'bg-slate-100 text-slate-700';
    if (status === 'cancelled') return 'bg-slate-100 text-slate-600';
    if (status === 'payment_due' || status === 'renewal_due') return 'bg-amber-50 text-amber-800';
    if (status === 'unpaid') return 'bg-rose-50 text-rose-700';
    return 'bg-orange-50 text-orange-800';
}

function actionCount(row: any) {
    let count = 0;
    if (row?.hasAccess) count += 1;
    if (!row?.parentCancelled && row?.paymentStatus === 'active' && row?.hasAccess) count += 1;
    if (!row?.parentCancelled && row?.paymentStatus === 'paused') count += 1;
    if (row?.parentCancelled || row?.paymentStatus === 'cancelled') count += 1;
    if (!row?.parentCancelled && row?.paymentStatus === 'unpaid') count += 1;
    if (!row?.parentCancelled && row?.paymentStatus !== 'paused' && row?.paymentStatus !== 'unpaid' && (!row?.hasAccess || row?.accessMode === 'grace' || row?.invoiceDue)) count += 1;
    if (!row?.parentCancelled) count += 1;
    return Math.max(count, 1);
}

export default function PrivateLessonsPage() {
    const userService = new UserService();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [draft, setDraft] = useState<any>(null);
    const personalModalRef = useRef<PersonalClassModalHandle>(null);
    const [cancellingId, setCancellingId] = useState<number | null>(null);
    const [actionMenu, setActionMenu] = useState<{ id: number; top: number; left: number } | null>(null);
    const [joinPrompt, setJoinPrompt] = useState<any>(null);
    const [cancelPrompt, setCancelPrompt] = useState<any>(null);
    const [statusFilter, setStatusFilter] = useState('all');

    const load = async () => {
        setLoading(true);
        try {
            const response = await userService.getMyPersonalClasses();
            if (response?.status && Array.isArray(response.data)) {
                setRows(response.data);
            }
        } catch {
            toast.error('Unable to load private lesson history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
    // Intentionally mount-only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!actionMenu) return;
        const close = () => setActionMenu(null);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [actionMenu]);

    const openActionMenu = (event: MouseEvent<HTMLButtonElement>, id: number) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const menuWidth = 256;
        const row = rows.find((item) => item.id === id);
        const menuHeight = actionCount(row) * 52;
        const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
        const openUp = window.innerHeight - rect.bottom < menuHeight + 12;
        setActionMenu({
            id,
            top: openUp ? rect.top - menuHeight - 6 : rect.bottom + 6,
            left,
        });
    };

    const cancelLesson = async (item: any) => {
        setCancellingId(item.id);
        try {
            const response = await userService.cancelPersonalClass(item.id);
            if (!response?.status) {
                toast.error(response?.message || 'Unable to cancel this registration');
                return;
            }
            if (response.data?.keptUntilPaid) {
                toast.success('Registration cancelled. Class stays open until the paid cycle ends.');
            } else {
                toast.success('Registration cancelled. You can activate it again from this list.');
            }
            setRows((current) => current.map((row) => (row.id === item.id ? response.data : row)));
        } catch (err: any) {
            toast.error(err?.message || 'Unable to cancel this registration');
        } finally {
            setCancellingId(null);
        }
    };

    const actionRow = rows.find((row) => row.id === actionMenu?.id);
    const filters = [
        { id: 'all', label: 'All' },
        { id: 'active', label: 'Active' },
        { id: 'paused', label: 'Paused' },
        { id: 'renewal_due', label: 'Renewal due' },
        { id: 'unpaid', label: 'Unpaid' },
        { id: 'cancelled', label: 'Cancelled' },
    ];
    const visible = rows.filter((item) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'cancelled') return Boolean(item.parentCancelled || item.paymentStatus === 'cancelled');
        if (statusFilter === 'renewal_due') return item.paymentStatus === 'renewal_due' || item.paymentStatus === 'payment_due';
        return item.paymentStatus === statusFilter;
    });

    return (
        <AppLayout>
            <PageHeader
                title="Private lessons"
                subtitle="Every private lesson, including the ones already showing on your dashboard."
                actionLabel="Private lesson"
                onAction={() => {
                    setDraft(null);
                    setShowModal(true);
                }}
            />
            {loading ? (
                <div className="relative flex h-40 items-center justify-center">
                    <div className="h-[72px] w-[72px] animate-spin rounded-full border border-ryd-primary border-l-white" />
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-[20px] border border-dashed border-[#d7e7f5] bg-white px-6 py-16 text-center">
                    <p className="font-[AvertaStd-Semibold] text-[18px] text-ryd-headerTextPrimary">
                        No private lessons yet
                    </p>
                    <p className="mt-2 text-[14px] text-[#8b9aab]">
                        Active and past lessons for all your children show up in this table.
                    </p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-[22px] border border-[#efe6f1] bg-white shadow-[0_12px_40px_rgba(136,22,122,0.06)]">
                    <div className="flex flex-wrap gap-2 border-b border-[#f0eaf0] px-4 py-3">
                        {filters.map((filter) => (
                            <button
                                key={filter.id}
                                type="button"
                                onClick={() => setStatusFilter(filter.id)}
                                className={`rounded-full px-3 py-1.5 text-[12px] font-semibold ${statusFilter === filter.id ? 'bg-ryd-primary text-white' : 'bg-[#F8F5FA] text-[#64748b]'}`}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[980px] border-collapse text-left">
                            <thead>
                                <tr className="border-b border-[#f0eaf0] bg-[#FBF8FC] text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
                                    <th className="px-4 py-4 font-semibold">Child</th>
                                    <th className="px-3 py-4 font-semibold">Lesson</th>
                                    <th className="px-3 py-4 font-semibold">Schedule</th>
                                    <th className="px-3 py-4 font-semibold">Teacher</th>
                                    <th className="px-3 py-4 font-semibold">Amount</th>
                                    <th className="px-3 py-4 font-semibold">Access</th>
                                    <th className="px-3 py-4 font-semibold">Status</th>
                                    <th className="px-4 py-4 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-10 text-center text-[14px] text-[#8b9aab]">No lessons in this view.</td>
                                    </tr>
                                ) : visible.map((item) => {
                                    const slots = slotList(item.slots);
                                    const teacher = [item?.teacher?.firstName, item?.teacher?.lastName].filter(Boolean).join(' ') || 'Not assigned';
                                    const subject = item.subject || item.package?.title || 'Private lesson';
                                    return (
                                        <tr key={item.id} className="border-b border-[#f3eef5] last:border-b-0 hover:bg-[#FBF8FC]/80">
                                            <td className="px-4 py-4">
                                                <p className="text-[14px] font-semibold capitalize text-ryd-headerTextPrimary">{item.child?.firstName} {item.child?.lastName}</p>
                                                <p className="mt-0.5 text-[12px] text-[#8b9aab]">{item.grade || '—'} · Age {item.child?.age ?? '—'}</p>
                                            </td>
                                            <td className="px-3 py-4">
                                                <p className="text-[13px] font-semibold text-[#334155]">{subject}</p>
                                                {item.package?.title && item.package.title !== subject ? (
                                                    <p className="mt-0.5 text-[12px] text-[#8b9aab]">{item.package.title}</p>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-4 text-[13px] text-[#334155]">
                                                {slots.length === 0 ? '—' : slots.map((slot, index) => (
                                                    <div key={index}>{slot.dayText || '—'} · {slot.timeText || '—'}</div>
                                                ))}
                                            </td>
                                            <td className="px-3 py-4 text-[13px] text-[#334155]">{teacher}</td>
                                            <td className="px-3 py-4 text-[13px] text-[#334155]">{item.currency || 'NGN'} {Number(item.amount || 0).toLocaleString()}</td>
                                            <td className="px-3 py-4 text-[13px] text-[#334155]">
                                                {item.paymentStatus === 'paused'
                                                    ? (item.pausedRemainingLabel || 'Paused')
                                                    : formatWhen(item.paidUntil)}
                                            </td>
                                            <td className="px-3 py-4">
                                                <span className={`inline-flex rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusPill(item.paymentStatus)}`}>
                                                    {statusText(item.paymentStatus)}
                                                </span>
                                                {item.parentCancelled && item.hasAccess ? (
                                                    <div className="mt-1 text-[12px] text-amber-700">Join until {formatWhen(item.paidUntil)}</div>
                                                ) : null}
                                            </td>
                                            <td className="px-4 py-4">
                                                <button
                                                    type="button"
                                                    aria-label="Lesson actions"
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#64748b] hover:bg-[#f3eef5]"
                                                    onClick={(event) => openActionMenu(event, item.id)}
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {actionMenu && actionRow && createPortal(
                <>
                    <button
                        type="button"
                        className="fixed inset-0 z-[80] cursor-default"
                        aria-label="Close actions"
                        onClick={() => setActionMenu(null)}
                    />
                    <div
                        className="fixed z-[90] w-64 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                        style={{ top: actionMenu.top, left: actionMenu.left }}
                    >
                        {actionRow.hasAccess && !actionRow.teacher ? (
                            <span className="block px-3 py-2 text-left text-sm text-slate-400" title="Teacher not assigned, please contact us at learning@rydlearning.com">
                                Join class
                                <span className="mt-1 block text-[11px] font-normal leading-snug text-slate-500">
                                    Teacher not assigned, please contact us at learning@rydlearning.com
                                </span>
                            </span>
                        ) : null}
                        {actionRow.hasAccess && actionRow.teacher ? (
                            <button
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                onClick={() => {
                                    if (actionRow.accessMode === 'grace') {
                                        setJoinPrompt(actionRow);
                                        setActionMenu(null);
                                        return;
                                    }
                                    setActionMenu(null);
                                    void (async () => {
                                        try {
                                            const response = await userService.joinPersonalClass(actionRow.id);
                                            const link = response?.data?.classLink;
                                            if (!response?.status || !link) {
                                                toast.error(response?.message || 'Unable to join this class');
                                                return;
                                            }
                                            window.open(link, '_blank', 'noopener,noreferrer');
                                        } catch (err: any) {
                                            toast.error(err?.message || 'Unable to join this class');
                                        }
                                    })();
                                }}
                            >
                                Join class
                            </button>
                        ) : null}
                        {!actionRow.parentCancelled && actionRow.paymentStatus === 'active' && actionRow.hasAccess ? (
                            <button
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                onClick={() => {
                                    setActionMenu(null);
                                    void (async () => {
                                        try {
                                            const response = await userService.pausePersonalClass(actionRow.id);
                                            if (!response?.status) {
                                                toast.error(response?.message || 'Unable to pause this lesson');
                                                return;
                                            }
                                            setRows((current) => current.map((item) => (item.id === actionRow.id ? response.data : item)));
                                            toast.success(response.message || 'Lesson paused');
                                        } catch (err: any) {
                                            toast.error(err?.message || 'Unable to pause this lesson');
                                        }
                                    })();
                                }}
                            >
                                Pause lesson
                            </button>
                        ) : null}
                        {!actionRow.parentCancelled && actionRow.paymentStatus === 'paused' ? (
                            <button
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                onClick={() => {
                                    setActionMenu(null);
                                    void (async () => {
                                        try {
                                            const response = await userService.resumePersonalClass(actionRow.id);
                                            if (!response?.status) {
                                                toast.error(response?.message || 'Unable to resume this lesson');
                                                return;
                                            }
                                            setRows((current) => current.map((item) => (item.id === actionRow.id ? response.data : item)));
                                            toast.success(response.message || 'Lesson resumed');
                                        } catch (err: any) {
                                            toast.error(err?.message || 'Unable to resume this lesson');
                                        }
                                    })();
                                }}
                            >
                                Resume{actionRow.pausedRemainingLabel ? ` · ${actionRow.pausedRemainingLabel}` : ''}
                            </button>
                        ) : null}
                        {actionRow.parentCancelled || actionRow.paymentStatus === 'cancelled' ? (
                            <button
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                onClick={() => {
                                    setActionMenu(null);
                                    setDraft({
                                        id: actionRow.id,
                                        child: actionRow.child,
                                        childId: actionRow.childId || actionRow.child?.id,
                                        grade: actionRow.grade || '',
                                        subject: actionRow.subject || actionRow.package?.title || '',
                                        package: actionRow.package,
                                        packageId: actionRow.packageId || actionRow.package?.id,
                                        classesPerWeek: Math.min(4, Math.max(1, Number(actionRow.classesPerWeek) || 2)),
                                        slots: [],
                                        prefillSlots: false,
                                    });
                                    setShowModal(true);
                                }}
                            >
                                Activate
                            </button>
                        ) : null}
                        {!actionRow.parentCancelled && actionRow.paymentStatus === 'unpaid' ? (
                            <button
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                onClick={() => {
                                    setActionMenu(null);
                                    void (async () => {
                                        try {
                                            const response = await userService.payPersonalClass(actionRow.id);
                                            if (!response?.status) {
                                                toast.error(response?.message || 'Unable to open checkout');
                                                return;
                                            }
                                            startPersonalCheckout(response.data);
                                        } catch (err: any) {
                                            toast.error(err?.message || 'Unable to open checkout');
                                        }
                                    })();
                                }}
                            >
                                Complete registration
                            </button>
                        ) : null}
                        {!actionRow.parentCancelled && actionRow.paymentStatus !== 'paused' && actionRow.paymentStatus !== 'unpaid' && (!actionRow.hasAccess || actionRow.accessMode === 'grace' || actionRow.invoiceDue) ? (
                            <button
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                onClick={() => {
                                    setActionMenu(null);
                                    void (async () => {
                                        try {
                                            const response = await userService.payPersonalClass(actionRow.id);
                                            if (!response?.status) {
                                                toast.error(response?.message || 'Unable to open checkout');
                                                return;
                                            }
                                            startPersonalCheckout(response.data);
                                        } catch (err: any) {
                                            toast.error(err?.message || 'Unable to open checkout');
                                        }
                                    })();
                                }}
                            >
                                Pay to continue
                            </button>
                        ) : null}
                        {!actionRow.parentCancelled ? (
                            <button
                                type="button"
                                className="block w-full px-3 py-1.5 text-left text-[12px] text-red-600 hover:bg-slate-50 disabled:opacity-60"
                                disabled={cancellingId === actionRow.id}
                                onClick={() => {
                                    setCancelPrompt(actionRow);
                                    setActionMenu(null);
                                }}
                            >
                                {cancellingId === actionRow.id ? 'Cancelling…' : 'Cancel registration'}
                            </button>
                        ) : null}
                    </div>
                </>,
                document.body
            )}

            {cancelPrompt ? (
                <CustomModal modalStyle={`${modalShell} max-w-md`} closeModal={() => setCancelPrompt(null)}>
                    <div className="p-6">
                        <h2 className="text-[18px] font-semibold text-ryd-headerTextPrimary">Cancel registration?</h2>
                        <p className="mt-2 text-[14px] text-[#334155]">
                            {cancelPrompt.paymentStatus === 'paused'
                                ? `The remaining paid time stays saved. Cancelling closes the lesson until you activate it and resume.`
                                : cancelPrompt.hasAccess
                                ? `${cancelPrompt.child?.firstName} ${cancelPrompt.child?.lastName} can still join class until ${formatWhen(cancelPrompt.paidUntil)}. After that date the lesson closes and no new invoice is sent.`
                                : `The lesson stays in this list. You can activate it later and finish payment for ${cancelPrompt.child?.firstName} ${cancelPrompt.child?.lastName}.`}
                        </p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" className="rounded-lg px-4 py-2 text-sm font-semibold text-[#64748b]" onClick={() => setCancelPrompt(null)}>Keep registration</button>
                            <button
                                type="button"
                                disabled={cancellingId === cancelPrompt.id}
                                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                                onClick={() => {
                                    const row = cancelPrompt;
                                    setCancelPrompt(null);
                                    void cancelLesson(row);
                                }}
                            >
                                {cancellingId === cancelPrompt.id ? 'Cancelling…' : 'Yes, cancel'}
                            </button>
                        </div>
                    </div>
                </CustomModal>
            ) : null}

            {joinPrompt ? (
                <CustomModal modalStyle={`${modalShell} max-w-md`} closeModal={() => setJoinPrompt(null)}>
                    <div className="p-6">
                        <h2 className="text-[18px] font-semibold text-ryd-headerTextPrimary">Payment is due</h2>
                        <p className="mt-2 text-[14px] text-[#334155]">{joinPrompt.joinWarning}</p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" className="rounded-lg px-4 py-2 text-sm font-semibold text-[#64748b]" onClick={() => setJoinPrompt(null)}>Not now</button>
                            <button
                                type="button"
                                className="rounded-lg bg-ryd-primary px-4 py-2 text-sm font-semibold text-white"
                                onClick={() => {
                                    const row = joinPrompt;
                                    setJoinPrompt(null);
                                    void (async () => {
                                        try {
                                            const response = await userService.joinPersonalClass(row.id);
                                            const link = response?.data?.classLink;
                                            if (!response?.status || !link) {
                                                toast.error(response?.message || 'Unable to join this class');
                                                return;
                                            }
                                            window.open(link, '_blank', 'noopener,noreferrer');
                                            void load();
                                        } catch (err: any) {
                                            toast.error(err?.message || 'Unable to join this class');
                                        }
                                    })();
                                }}
                            >
                                Join this class
                            </button>
                        </div>
                    </div>
                </CustomModal>
            ) : null}

            {showModal ? (
                <CustomModal modalStyle={`${modalShell} max-w-xl`} closeModal={() => personalModalRef.current?.requestClose()}>
                    <PersonalClassModal
                        ref={personalModalRef}
                        key={draft?.id || 'new'}
                        initialEnrollment={draft}
                        onClose={() => setShowModal(false)}
                        onPaid={() => { void load(); }}
                    />
                </CustomModal>
            ) : null}
        </AppLayout>
    );
}
